import { ListRowThumbnailColumn } from "@/components/ui/ListRowThumbnailColumn";
import { Lesson, UserLesson } from "@/types";
import { lessonThumbnailProgressPercent } from "@/utils/lessonProgressPercent";
import {
  getLessonStructuredTitleMeta,
  LESSON_META_COLOR,
} from "@/utils/lessonStructuredTitle";
import React, { memo, useState } from "react";
import { Platform, Pressable } from "react-native";
import { SizableText, XStack, YStack } from "tamagui";
import {
  BodyText,
  CaptionText,
  CardTitle,
  EyebrowText,
} from "@/components/ui/Typography";

// Pointer can hover (desktop web): reveal the remove button on hover only.
// Touch devices (native or mobile web) get it always visible.
const HAS_HOVER =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(hover: hover)").matches;

interface LessonListRowProps {
  item: Lesson;
  onPress: () => void;
  userLesson?: UserLesson;
  isMobile: boolean;
  hidePublishedDate?: boolean;
  showCourseColor?: boolean;
  /** When set, shows a trailing ✕ (e.g. remove from Continue watching). */
  onRemove?: () => void;
}

export const LessonListRow = memo(function LessonListRow({
  item,
  onPress,
  userLesson,
  isMobile,
  hidePublishedDate = false,
  showCourseColor = false,
  onRemove,
}: LessonListRowProps) {
  const [hovered, setHovered] = useState(false);
  const { displayTitle, metaLine, mainWorkTitle, weekLabel } =
    getLessonStructuredTitleMeta(item);
  const hasStructuredMeta = !!metaLine;
  const titleLineCount = isMobile ? 3 : 1;

  const progress = userLesson
    ? lessonThumbnailProgressPercent({
        userProgress: userLesson.progress,
        videoCount: item.video_count,
        completedVideoCount: userLesson.completed_video_count,
        leftOffAtSeconds: userLesson.left_off_at_seconds,
        durationSeconds: item.duration,
      })
    : (item.progressPercentage ?? 0);

  const titleText = (
    <CardTitle
      fontWeight="600"
      color={showCourseColor && item.course_slug ? "#987451" : "#0D0D0D"}
      numberOfLines={titleLineCount}
      ellipsizeMode="tail"
      overflow="hidden"
    >
      {displayTitle}
    </CardTitle>
  );

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={onRemove && HAS_HOVER ? () => setHovered(true) : undefined}
      onHoverOut={onRemove && HAS_HOVER ? () => setHovered(false) : undefined}
    >
      <XStack
        ai="stretch"
        bg="#fff"
        br={10}
        boxShadow="0 1px 6px rgba(0,0,0,0.08)"
        overflow="hidden"
      >
        <ListRowThumbnailColumn
          thumbnailUri={item.thumbnail}
          aspectRatio={16 / 9}
          progress={progress}
          showCheckmark={!!userLesson?.is_completed}
          imageEngine="tamagui"
          showEmptyPlaceholder={false}
        />

        <YStack flex={1} p={16} gap={2} jc="center">
          <XStack ai="flex-start" jc="space-between" gap={12}>
            <YStack flex={1} minWidth={0}>
              {hasStructuredMeta ? (
                mainWorkTitle && weekLabel ? (
                  <XStack alignItems="center" flexWrap="nowrap" width="100%">
                    <EyebrowText
                      color={LESSON_META_COLOR}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      overflow="hidden"
                      flexShrink={1}
                      minWidth={0}
                    >
                      {mainWorkTitle}
                    </EyebrowText>
                    <EyebrowText
                      color={LESSON_META_COLOR}
                      flexShrink={0}
                    >
                      {` - ${weekLabel}`}
                    </EyebrowText>
                  </XStack>
                ) : (
                  <EyebrowText
                    color={LESSON_META_COLOR}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    overflow="hidden"
                  >
                    {metaLine}
                  </EyebrowText>
                )
              ) : (
                titleText
              )}
            </YStack>
            {!isMobile && item.published_at && !hidePublishedDate && (
              <CaptionText
                ml={8}
                color="#655D56"
                numberOfLines={1}
              >
                {new Date(item.published_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  timeZone: "UTC",
                })}
              </CaptionText>
            )}
          </XStack>
          {hasStructuredMeta ? titleText : null}
          {!isMobile && !!item.summary && (
            <BodyText
              color="#0D0D0D"
              mt={4}
              numberOfLines={3}
              ellipsizeMode="tail"
              style={{ overflow: "hidden" }}
            >
              {item.summary}
            </BodyText>
          )}
        </YStack>

        {onRemove ? (
          <YStack jc="center" pr={12}>
            <YStack
              w={24}
              h={24}
              br={12}
              bg="rgba(0,0,0,0.55)"
              jc="center"
              ai="center"
              opacity={!HAS_HOVER || hovered ? 1 : 0}
              accessibilityLabel="Remove from Continue watching"
              style={{ cursor: "pointer" }}
              hoverStyle={{ bg: "rgba(0,0,0,0.8)" }}
              pressStyle={{ opacity: 0.8 }}
              onPress={(e: any) => {
                e?.stopPropagation?.();
                onRemove();
              }}
            >
              <SizableText color="#fff" fontSize={12} lineHeight={14}>
                ✕
              </SizableText>
            </YStack>
          </YStack>
        ) : null}
      </XStack>
    </Pressable>
  );
});
